/**
 * Pure JRiver shortcut matching (no transport imports — safe for unit tests).
 */

import { isFindShortcut } from '../utils/platform';

export const JRIVER_SEEK_STEP_SEC = 10;
export const JRIVER_JUMP_STEP_SEC = 30;

export type JriverShortcutAction =
  | 'playPause'
  | 'stop'
  | 'previous'
  | 'next'
  | 'muteToggle'
  | 'volumeUp'
  | 'volumeDown'
  | 'seekBack'
  | 'seekForward'
  | 'jumpBack'
  | 'jumpForward'
  | 'find'
  | 'cycleZone';

export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el?.tagName) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  return !!el.isContentEditable;
}

/** JRiver uses Control (not Command) on macOS as well as Windows. */
export function isJriverCtrl(e: Pick<KeyboardEvent, 'ctrlKey' | 'metaKey' | 'altKey'>): boolean {
  return e.ctrlKey && !e.metaKey && !e.altKey;
}

export function matchJriverShortcut(e: Pick<KeyboardEvent, 'key' | 'code' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>): JriverShortcutAction | null {
  if (isFindShortcut(e)) return 'find';

  if (!isJriverCtrl(e)) return null;

  if (e.shiftKey) {
    if (e.key === 'ArrowLeft') return 'jumpBack';
    if (e.key === 'ArrowRight') return 'jumpForward';
    return null;
  }

  switch (e.key) {
    case 'p':
    case 'P':
      return 'playPause';
    case 's':
    case 'S':
      return 'stop';
    case 'l':
    case 'L':
      return 'previous';
    case 'n':
    case 'N':
      return 'next';
    case 'm':
    case 'M':
      return 'muteToggle';
    case 'f':
    case 'F':
      return 'find';
    case 't':
    case 'T':
      return 'cycleZone';
    case '+':
    case '=':
      return 'volumeUp';
    case '-':
    case '_':
      return 'volumeDown';
    case 'ArrowLeft':
      return 'seekBack';
    case 'ArrowRight':
      return 'seekForward';
    default:
      break;
  }

  if (e.code === 'NumpadAdd' || e.code === 'Equal') return 'volumeUp';
  if (e.code === 'NumpadSubtract' || e.code === 'Minus') return 'volumeDown';

  return null;
}
